/**
 * quoteSplitter — separates the "new" part of an email from its quoted history.
 *
 * Used in two places:
 *   - Display: collapse the embedded quote inside each thread message (the
 *     previous messages are already shown as their own cards, so repeating
 *     their full text inside every reply is just noise).
 *   - Send: when we quote the message being replied to, include only its own
 *     new content — not the whole nested chain it already carries.
 *
 * Everything here is best-effort and never throws: if we can't confidently find
 * a quote boundary, we return the full content as "main" and `hasQuote: false`,
 * so nothing is ever hidden by mistake.
 */

// Intro lines that introduce a quote, across the common locales/clients.
// Kept in sync with functions/helpers/replyBodyCleaner.js (quote boundary
// only — signature trimming stays in the Reply Report cleaner).
//   "Am 1.2.2026, 10:00 schrieb Max <max@x>:"  (Apple/German)
//   "On Mon, ... wrote:"                          (Gmail/English)
//   "Le ... a écrit :"                            (French)
//   "El ... escribió:"                            (Spanish)
//   "-------- Original Message --------"          (Outlook / classic)
// "Von:" / "Gesendet:" on separate lines is detected below, not here —
// a bare "Von:" inside a sentence must not hide the reply.
const QUOTE_INTRO_RE =
  /^\s*(am\s.+\sschrieb.*:|on\s.+\swrote:?|le\s.+\sa\s+écrit\s*:|el\s.+\sescribió\s*:|-{2,}\s*(?:original message|ursprüngliche nachricht|urspruengliche nachricht|forwarded message|weitergeleitete nachricht)\s*-{2,}|begin forwarded message:?|anfang der weitergeleiteten nachricht:?)\s*$/i;

// Date check is kept identical to functions/helpers/replyBodyCleaner.js.
const OUTLOOK_FROM_RE = /^\s*(?:from|von|de|van|fra|da)\s*:\s*\S+/i;
const OUTLOOK_SENT_LABEL_RE = /^\s*(?:sent|gesendet|envoyé|envoye|verzonden|sendt)\s*:/i;
const OUTLOOK_DATE_LABEL_RE = /^\s*(?:date|datum)\s*:/i;
const INLINE_OUTLOOK_RE =
  /^\s*(?:from|von|de|van|fra|da)\s*:\s*\S.*@\S.*\s+(?:sent|gesendet|envoyé|envoye|verzonden|sendt|date|datum)\s*:/i;
const QUOTE_SEPARATOR_RE = /^[_\-=]{5,}\s*$/;
const HEADER_DATE_WORD_RE = /\b(?:montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|monday|tuesday|wednesday|thursday|friday|saturday|sunday|januar|februar|märz|maerz|april|mai|juni|juli|august|september|oktober|november|dezember|january|february|march|may|june|july|august|october|december|sept?)\b/i;
const NUMERIC_DATE_RE = /\b\d{1,2}[./]\d{1,2}(?:[./]\d{2,4})?\b|\b\d{4}-\d{2}-\d{2}\b/;

const normLine = (line) =>
  String(line || '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();

const looksLikeHeaderDate = (line) => {
  const match = String(line).match(/\b(?:sent|gesendet|envoyé|envoye|verzonden|sendt|date|datum)\s*:\s*(.*)$/i);
  const portion = match ? match[1].trim() : '';
  if (!portion || !/\d/.test(portion)) return false;
  if (portion.split(/\s+/).length > 7) return false;
  if (/[?]/.test(portion)) return false;
  return HEADER_DATE_WORD_RE.test(portion) || NUMERIC_DATE_RE.test(portion);
};

const isOutlookHeaderStart = (lines, index) => {
  const line = normLine(lines[index]);
  if (INLINE_OUTLOOK_RE.test(line) && looksLikeHeaderDate(line)) return true;
  if (!OUTLOOK_FROM_RE.test(line)) return false;
  const hasAddress = /@|mailto:/i.test(line);
  if (!hasAddress && line.length > 80) return false;
  let seen = 0;
  for (let j = index + 1; j < lines.length && j < index + 10; j++) {
    const next = normLine(lines[j]);
    if (!next) continue;
    seen += 1;
    const sentLabel = OUTLOOK_SENT_LABEL_RE.test(next);
    const dateLabel = OUTLOOK_DATE_LABEL_RE.test(next) && hasAddress;
    if ((sentLabel || dateLabel) && looksLikeHeaderDate(next)) return true;
    if (seen >= 5) break;
  }
  return false;
};

const isSeparatorBeforeQuote = (lines, index) => {
  if (!QUOTE_SEPARATOR_RE.test(normLine(lines[index]))) return false;
  for (let j = index + 1; j < lines.length && j < index + 5; j++) {
    const next = normLine(lines[j]);
    if (!next) continue;
    return QUOTE_INTRO_RE.test(next) || isOutlookHeaderStart(lines, j) || /^>/.test(next);
  }
  return false;
};

const findQuoteBoundary = (lines) => {
  for (let i = 0; i < lines.length; i++) {
    const line = normLine(lines[i]);
    if (!line) continue;
    if (QUOTE_INTRO_RE.test(line) || /^>/.test(line) || isOutlookHeaderStart(lines, i) || isSeparatorBeforeQuote(lines, i)) {
      return i;
    }
  }
  return -1;
};

const QUOTE_CLASS_RE = /(gmail_quote|gmail_extra|moz-cite-prefix|yahoo_quoted|zmail_extra)/i;
const QUOTE_ID_RE = /(appendonsend|divrplyfwdmsg|mail-editor-reference-message-container)/i;

const hasDomParser = () =>
  typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined';

const isIntroElement = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
  // Only treat as an intro line if it's short — avoids matching a huge block
  // that merely starts with "Am …" or "Von:".
  if (!text || text.length >= 300) return false;
  const lines = text.split(/\r?\n/);
  const first = normLine(lines[0]);
  return QUOTE_INTRO_RE.test(first) || isOutlookHeaderStart(lines, 0);
};

const isQuoteContainer = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (el.tagName === 'BLOCKQUOTE') return true;
  const cls = (el.getAttribute && el.getAttribute('class')) || '';
  if (QUOTE_CLASS_RE.test(cls)) return true;
  const id = (el.getAttribute && el.getAttribute('id')) || '';
  if (QUOTE_ID_RE.test(id)) return true;
  return false;
};

/**
 * Split an HTML body into { mainHtml, quotedHtml, hasQuote }.
 */
export function splitHtmlQuote(html) {
  const fallback = { mainHtml: html || '', quotedHtml: '', hasQuote: false };
  if (!html || typeof html !== 'string' || !hasDomParser()) return fallback;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return fallback;

    const children = Array.from(body.children);
    if (children.length === 0) return fallback;

    // 1. Find the first top-level element that is (or contains) a quote.
    let boundary = -1;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      if (isQuoteContainer(el) || el.querySelector('blockquote') || isIntroElement(el)) {
        boundary = i;
        break;
      }
    }

    if (boundary === -1) return fallback;

    // 2. If the element right before the boundary is the "… schrieb:" intro
    //    line (a separate sibling), fold it into the quoted part too.
    if (boundary > 0 && isIntroElement(children[boundary - 1])) {
      boundary -= 1;
    }

    if (boundary <= 0) {
      // The whole body is the quote — nothing meaningful to show as "main".
      // Keep showing everything rather than an empty card.
      return fallback;
    }

    const mainHtml = children.slice(0, boundary).map((el) => el.outerHTML).join('');
    const quotedHtml = children.slice(boundary).map((el) => el.outerHTML).join('');

    if (!mainHtml.trim() || !quotedHtml.trim()) return fallback;

    return { mainHtml, quotedHtml, hasQuote: true };
  } catch (_) {
    return fallback;
  }
}

/**
 * Split a plain-text body into { mainText, quotedText, hasQuote }.
 */
export function splitTextQuote(text) {
  const fallback = { mainText: text || '', quotedText: '', hasQuote: false };
  if (!text || typeof text !== 'string') return fallback;

  const lines = text.split(/\r?\n/);
  const boundary = findQuoteBoundary(lines);

  if (boundary <= 0) return fallback;

  const mainText = lines.slice(0, boundary).join('\n').trim();
  const quotedText = lines.slice(boundary).join('\n').trim();

  if (!mainText || !quotedText) return fallback;

  return { mainText, quotedText, hasQuote: true };
}
